import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useOutletContext, useLocation } from 'react-router-dom';
import { Mail, Lock, User, UserPlus, Phone, AlertCircle, Loader2, Globe, Eye, EyeOff } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import api from '../api';
import { PhoneInput } from '../components/PhoneInput';
import { RegisterContextType } from './RegisterLayout';
import BrandLogo from '../components/BrandLogo';
import { analytics } from '../analytics';
import { useAuth } from '../context/AuthContext';
import { authLink, authReturnPath, clearAuthReturnPath, rememberAuthReturnPath } from '../utils/authReturnPath';

export default function RegisterPage() {
  const { t, language, toggleLanguage } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const { refreshUser } = useAuth();
  const [redirectTo] = useState(() => authReturnPath(location.search, location.state));
  const { formData, setFormData } = useOutletContext<RegisterContextType>();
  const [errorMsg, setErrorMsg] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isGoogleRedirecting, setIsGoogleRedirecting] = useState(false);
  const [isHandlingGoogle, setIsHandlingGoogle] = useState(() => window.location.hash.includes('id_token='));
  const [submitCooldown, setSubmitCooldown] = useState(0);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Google Setup Form State
  const [googleSetupData, setGoogleSetupData] = useState<{idToken: string, firstName: string, lastName: string} | null>(null);
  const [googlePhone, setGooglePhone] = useState('');
  const [googleTermsAccepted, setGoogleTermsAccepted] = useState(false);
  const [isGoogleCompleting, setIsGoogleCompleting] = useState(false);

  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (submitCooldown > 0) {
      timer = setTimeout(() => setSubmitCooldown(c => c - 1), 1000);
    }
    return () => clearTimeout(timer);
  }, [submitCooldown]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((prev: any) => ({ ...prev, [e.target.name]: e.target.value }));
    setErrorMsg('');
  };

  // Handle standard OAuth 2.0 Implicit Flow return from Google
  useEffect(() => {
    const hash = window.location.hash;
    if (hash.includes('id_token=')) {
      setIsHandlingGoogle(true);
      const params = new URLSearchParams(hash.substring(1));
      const idToken = params.get('id_token');
      if (idToken) {
        window.history.replaceState(null, '', window.location.pathname);
        api.post('/auth/google', { idToken })
          .then(async res => {
            if (res.data.agreed_to_terms === false) {
              setGoogleSetupData({
                idToken,
                firstName: res.data.user?.name || '',
                lastName: res.data.user?.surname || ''
              });
            } else {
              localStorage.setItem('access_token', res.data.access_token);
              await refreshUser();
              clearAuthReturnPath();
              navigate(redirectTo);
            }
          })
          .catch(error => {
            setErrorMsg(t('errors.google_auth_failed'));
            setIsHandlingGoogle(false);
          });
      }
    }
  }, [navigate, t, refreshUser, redirectTo]);

  const handleGoogleLogin = () => {
    const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
    if (!clientId) {
      setErrorMsg(t('errors.google_client_id_missing'));
      return;
    }

    setIsGoogleRedirecting(true);
    rememberAuthReturnPath(redirectTo);
    // Dynamic redirect URI - adapts to localhost, 127.0.0.1, or remote domains dynamically
    const redirectUri = window.location.origin + '/register';
    const nonce = Math.random().toString(36).substring(2) + Date.now().toString(36);
    
    // prompt=select_account forces Google to show the account selection screen every time
    const authUrl = `https://accounts.google.com/o/oauth2/v2/auth?client_id=${clientId}&redirect_uri=${encodeURIComponent(redirectUri)}&response_type=id_token&scope=openid%20email%20profile&nonce=${nonce}&prompt=select_account`;
    window.location.href = authUrl;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!termsAccepted) {
      setErrorMsg(t('auth.termsNeedToAcceptError'));
      return;
    }
    if (submitCooldown > 0) return;
    
    if (formData.password.length < 8 || !/[A-Z]/.test(formData.password) || !/\d/.test(formData.password)) {
      setErrorMsg(t('auth.passwordRequirements'));
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setErrorMsg(t('errors.passwords_mismatch'));
      return;
    }
    
    setIsLoading(true);
    try {
      await api.post('/auth/register', {
        email: formData.email,
        password: formData.password,
        name: formData.firstName,
        surname: formData.lastName,
        phone_number: formData.phone,
        agreed_to_terms: termsAccepted
      });
      analytics.signUp('email');
      navigate(authLink('/verify-email', redirectTo));
    } catch (error: any) {
      if (error.response?.status === 429) {
        const detail = error.response?.data?.detail;
        setErrorMsg(t('errors.too_many_requests'));
        let waitSecs = 60; // default 1 min
        if (typeof detail === 'string') {
           const match = detail.match(/(\d+)\s*(min|sec)/i);
           if (match) {
             waitSecs = match[2].toLowerCase().startsWith('min') ? parseInt(match[1]) * 60 : parseInt(match[1]);
           }
        }
        setSubmitCooldown(waitSecs);
      } else if (error.response?.status === 422) {
        const detail = error.response?.data?.detail;
        if (Array.isArray(detail)) {
          setErrorMsg(detail[0].msg);
        } else if (typeof detail === 'string') {
          setErrorMsg(detail);
        } else {
          setErrorMsg(t('auth.passwordRequirements'));
        }
      } else {
        const detail = error.response?.data?.detail;
        // Typically backend returns English details, mapping them:
        if (detail === "REGISTER_USER_ALREADY_EXISTS" || detail === "The user with this username already exists in the system.") {
          setErrorMsg(t('errors.user_exists'));
        } else {
          console.error("Registration failed:", error.response?.data || error.message);
          // Fallback to default localized error if detail is raw english text
          setErrorMsg(t('errors.default'));
        }
      }
      setIsLoading(false);
    }
  };

  const handleGoogleSetupSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!googleTermsAccepted || !googleSetupData) {
      setErrorMsg(t('auth.termsNeedToAcceptError'));
      return;
    }
    if (!googlePhone || googlePhone.replace(/\D/g, '').length < 11) {
      setErrorMsg(t('auth.phoneRequiredError'));
      return;
    }
    setIsGoogleCompleting(true);
    try {
      const res = await api.post('/auth/google/complete', {
        idToken: googleSetupData.idToken,
        phone_number: googlePhone,
        name: googleSetupData.firstName,
        surname: googleSetupData.lastName,
        agreed_to_terms: true
      });
      localStorage.setItem('access_token', res.data.access_token);
      analytics.signUp('google');
      await refreshUser();
      clearAuthReturnPath();
      navigate(redirectTo);
    } catch (error: any) {
      setErrorMsg(error.response?.data?.detail || t('errors.default'));
      setIsGoogleCompleting(false);
    }
  };

  if (isHandlingGoogle) {
    if (googleSetupData) {
      return (
        <div className="min-h-screen bg-slate-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8 font-sans relative overflow-hidden">
          <div className="absolute top-0 left-0 w-full h-full overflow-hidden z-0 pointer-events-none">
            <div className="absolute -top-40 -right-40 w-96 h-96 bg-goszakup-600/10 rounded-full blur-3xl"></div>
            <div className="absolute top-1/2 -left-20 w-80 h-80 bg-blue-500/10 rounded-full blur-3xl"></div>
          </div>
          
          <div className="sm:mx-auto sm:w-full sm:max-w-md relative z-10">
            <div className="flex justify-center pt-6">
              <Link to="/" className="flex items-center gap-3 text-goszakup-600 hover:text-goszakup-700 transition-colors group">
                <div className="bg-white p-3 rounded-2xl shadow-sm border border-gray-100 group-hover:shadow-md transition-all">
                  <BrandLogo variant="full" className="h-24 w-auto" />
                </div>
              </Link>
            </div>
            <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
              {t('auth.googleSetupTitle')}
            </h2>
            <p className="mt-2 text-center text-sm text-gray-600">
              {t('auth.alreadyHaveAccount')}{' '}
              <Link to={authLink('/login', redirectTo)} className="font-medium text-goszakup-600 hover:text-goszakup-500 transition-colors">
                {t('auth.loginToSystem')}
              </Link>
            </p>
          </div>

          <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md relative z-10">
            <div className="bg-white py-8 px-4 shadow-xl shadow-gray-200/50 sm:rounded-3xl sm:px-10 border border-gray-100 w-full animate-in fade-in zoom-in duration-500">
              <p className="text-sm text-gray-500 text-center mb-6">{t('auth.googleSetupDesc')}</p>
              
              {errorMsg && (
                <div className="mb-4 w-full bg-red-50 text-red-600 p-3 rounded-xl flex items-center gap-2 text-sm border border-red-100">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  <span>{errorMsg}</span>
                </div>
              )}

              <form onSubmit={handleGoogleSetupSubmit} className="space-y-5 w-full">
                <div>
                  <label className="block text-sm font-medium text-gray-700">{t('auth.phoneLabel')}</label>
                  <PhoneInput value={googlePhone} onChange={setGooglePhone} />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">{t('auth.firstNameLabel')}</label>
                    <input type="text" value={googleSetupData.firstName} onChange={(e) => setGoogleSetupData({...googleSetupData, firstName: e.target.value})} className="mt-1 block w-full px-3 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-goszakup-500" placeholder="Иван" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">{t('auth.lastNameLabel')}</label>
                    <input type="text" value={googleSetupData.lastName} onChange={(e) => setGoogleSetupData({...googleSetupData, lastName: e.target.value})} className="mt-1 block w-full px-3 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-goszakup-500" placeholder="Иванов" />
                  </div>
                </div>

                <div className="flex items-start gap-3 bg-gray-50 p-4 rounded-xl border border-gray-100">
                  <div className="flex items-center h-5">
                    <input
                      id="google-terms"
                      type="checkbox"
                      checked={googleTermsAccepted}
                      onChange={(e) => {
                        setGoogleTermsAccepted(e.target.checked);
                        setErrorMsg('');
                      }}
                      className="w-5 h-5 text-goszakup-600 bg-white border-gray-300 rounded focus:ring-goszakup-500 focus:ring-2"
                    />
                  </div>
                  <label htmlFor="google-terms" className="text-xs text-gray-600 leading-relaxed cursor-pointer">
                    {t('auth.registerTermsHint1')}
                    <Link to="/offer" className="font-medium text-goszakup-600 hover:text-goszakup-500 transition-colors" target="_blank">{t('auth.registerTermsOffer')}</Link>
                    {t('auth.registerTermsHint2')}
                    <Link to="/privacy" className="font-medium text-goszakup-600 hover:text-goszakup-500 transition-colors" target="_blank">{t('auth.registerTermsPrivacy')}</Link>
                    {t('auth.registerTermsHint3')}
                  </label>
                </div>

                <button type="submit" disabled={isGoogleCompleting} className="w-full flex justify-center items-center py-3 px-4 border border-transparent rounded-xl shadow-sm text-sm font-bold text-white bg-goszakup-600 hover:bg-goszakup-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-goszakup-500 transition-all disabled:opacity-75 disabled:cursor-not-allowed">
                  {isGoogleCompleting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  {t('auth.finishBtn')}
                </button>
              </form>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="min-h-screen bg-slate-50 flex flex-col justify-center items-center py-12 sm:px-6 lg:px-8 font-sans relative overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden z-0 pointer-events-none">
          <div className="absolute -top-40 -right-40 w-96 h-96 bg-goszakup-600/10 rounded-full blur-3xl"></div>
          <div className="absolute top-1/2 -left-20 w-80 h-80 bg-blue-500/10 rounded-full blur-3xl animate-pulse"></div>
        </div>
        
        <div className="relative z-10 flex flex-col items-center">
          <div className="bg-white p-8 rounded-3xl shadow-xl shadow-gray-200/50 border border-gray-100 flex flex-col items-center animate-in fade-in zoom-in duration-500">
            <Loader2 className="w-16 h-16 text-goszakup-600 animate-spin mb-6" />
            <h2 className="text-2xl font-extrabold text-gray-900 mb-2">{t('auth.googleAuthTitle')}</h2>
            <p className="text-gray-500 text-center max-w-xs">{t('auth.googleAuthDesc')}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8 font-sans relative overflow-hidden">
      {/* Background decorations */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden z-0 pointer-events-none">
        <div className="absolute -top-40 -right-40 w-96 h-96 bg-goszakup-600/10 rounded-full blur-3xl"></div>
        <div className="absolute top-1/2 -left-20 w-80 h-80 bg-blue-500/10 rounded-full blur-3xl"></div>
      </div>

      {/* Language Toggle */}
      <div className="absolute top-6 right-6 lg:top-8 lg:right-8 z-20">
        <button 
          onClick={toggleLanguage} 
          type="button"
          className="flex items-center gap-2 px-4 py-2 bg-white/80 backdrop-blur-sm text-gray-700 border border-gray-200 rounded-xl hover:bg-white hover:text-goszakup-600 hover:border-goszakup-300 transition-all shadow-sm font-medium"
        >
          <Globe className="w-4 h-4" />
          <span>{language === 'ru' ? 'РУ' : 'ҚАЗ'}</span>
        </button>
      </div>

      <div className="sm:mx-auto sm:w-full sm:max-w-md relative z-10">
        <div className="flex justify-center pt-6">
          <Link to="/" className="flex items-center gap-3 text-goszakup-600 hover:text-goszakup-700 transition-colors group">
            <div className="bg-white p-3 rounded-2xl shadow-sm border border-gray-100 group-hover:shadow-md transition-all">
              <BrandLogo variant="full" className="h-24 w-auto" />
            </div>
          </Link>
        </div>
        <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
          {t('auth.registerTitle')}
        </h2>
        <p className="mt-2 text-center text-sm text-gray-600">
          {t('auth.alreadyHaveAccount')}{' '}
          <Link to={authLink('/login', redirectTo)} className="font-medium text-goszakup-600 hover:text-goszakup-500 transition-colors">
            {t('auth.loginToSystem')}
          </Link>
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md relative z-10">
        <div className="bg-white py-8 px-4 shadow-xl shadow-gray-200/50 sm:rounded-3xl sm:px-10 border border-gray-100">
          {errorMsg && (
            <div className="mb-4 bg-red-50 text-red-600 p-3 rounded-xl flex items-center gap-2 text-sm border border-red-100">
              <AlertCircle className="w-4 h-4" />
              {errorMsg}
            </div>
          )}
          <form className="space-y-5" onSubmit={handleSubmit}>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="firstName" className="block text-sm font-medium text-gray-700">
                  {t('auth.firstNameLabel')}
                </label>
                <div className="mt-1 relative rounded-xl shadow-sm">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <User className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    id="firstName"
                    name="firstName"
                    type="text"
                    required
                    value={formData.firstName}
                    onChange={handleChange}
                    placeholder="Иван"
                    className="block w-full pl-10 pr-3 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-goszakup-500 focus:border-transparent sm:text-sm transition-all"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="lastName" className="block text-sm font-medium text-gray-700">
                  {t('auth.lastNameLabel')}
                </label>
                <div className="mt-1 relative rounded-xl shadow-sm">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <User className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    id="lastName"
                    name="lastName"
                    type="text"
                    required
                    value={formData.lastName}
                    onChange={handleChange}
                    placeholder="Иванов"
                    className="block w-full pl-10 pr-3 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-goszakup-500 focus:border-transparent sm:text-sm transition-all"
                  />
                </div>
              </div>
            </div>

            <div>
              <label htmlFor="phone" className="block text-sm font-medium text-gray-700">
                {t('auth.phoneLabel')}
              </label>
              <div className="mt-1 relative rounded-xl shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Phone className="h-5 w-5 text-gray-400 z-10" />
                </div>
                <PhoneInput
                  id="phone"
                  name="phone"
                  required
                  value={formData.phone}
                  onChange={(val) => setFormData((prev: any) => ({ ...prev, phone: val }))}
                  className="pl-10 pr-3"
                />
              </div>
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                {t('auth.emailLabel')}
              </label>
              <div className="mt-1 relative rounded-xl shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Mail className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="name@example.com"
                  className="block w-full pl-10 pr-3 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-goszakup-500 focus:border-transparent sm:text-sm transition-all"
                />
              </div>
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                {t('auth.passwordLabel')}
              </label>
              <div className="mt-1 relative rounded-xl shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  autoComplete="new-password"
                  required
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="••••••••"
                  className="block w-full pl-10 pr-10 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-goszakup-500 focus:border-transparent sm:text-sm transition-all"
                />
                <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="text-gray-400 hover:text-gray-500 focus:outline-none"
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
              </div>
              {formData.password && (formData.password.length < 8 || !/[A-Z]/.test(formData.password) || !/\d/.test(formData.password)) && (
                <p className="mt-1 text-xs text-red-500">{t('auth.passwordRequirements')}</p>
              )}
            </div>

            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
                {t('auth.confirmPasswordLabel')}
              </label>
              <div className="mt-1 relative rounded-xl shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="confirmPassword"
                  name="confirmPassword"
                  type={showConfirmPassword ? "text" : "password"}
                  autoComplete="new-password"
                  required
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  placeholder="••••••••"
                  className="block w-full pl-10 pr-10 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-goszakup-500 focus:border-transparent sm:text-sm transition-all"
                />
                <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="text-gray-400 hover:text-gray-500 focus:outline-none"
                    tabIndex={-1}
                  >
                    {showConfirmPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
              </div>
            </div>

            <div className="flex items-start">
              <div className="flex items-center h-5">
                <input
                  id="terms"
                  name="terms"
                  type="checkbox"
                  required
                  checked={termsAccepted}
                  onChange={(e) => setTermsAccepted(e.target.checked)}
                  className="focus:ring-goszakup-500 h-4 w-4 text-goszakup-600 border-gray-300 rounded cursor-pointer mt-1"
                />
              </div>
              <div className="ml-3 text-sm">
                <label htmlFor="terms" className="text-gray-500 font-medium leading-relaxed block cursor-pointer">
                  {t('auth.registerTermsHint1')}
                  <Link to="/offer" target="_blank" className="font-bold text-goszakup-600 hover:text-goszakup-700 underline underline-offset-2">
                    {t('auth.registerTermsOffer')}
                  </Link>
                  {t('auth.registerTermsHint2')}
                  <Link to="/privacy" target="_blank" className="font-bold text-goszakup-600 hover:text-goszakup-700 underline underline-offset-2">
                    {t('auth.registerTermsPrivacy')}
                  </Link>
                  {t('auth.registerTermsHint3')}
                </label>
              </div>
            </div>

            <div>
              <button
                type="submit"
                disabled={isLoading || isGoogleRedirecting || submitCooldown > 0}
                className="w-full flex justify-center items-center py-3 px-4 border border-transparent rounded-xl shadow-sm text-sm font-bold text-white bg-goszakup-600 hover:bg-goszakup-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-goszakup-500 transition-all duration-300 hover:scale-[1.02] disabled:opacity-75 disabled:hover:scale-100 disabled:cursor-not-allowed"
              >
                {isLoading ? (
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                ) : (
                  <UserPlus className="w-5 h-5 mr-2" />
                )}
                {isLoading ? t('auth.registering') : (submitCooldown > 0 ? `${t('auth.registerBtn')} (${submitCooldown}с)` : t('auth.registerBtn'))}
              </button>
            </div>
          </form>

          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-200" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white text-gray-500">{t('auth.registerVia')}</span>
              </div>
            </div>

            <div className="mt-6 flex justify-center w-full">
              <button
                type="button"
                onClick={handleGoogleLogin}
                disabled={isLoading || isGoogleRedirecting}
                className="w-full flex justify-center items-center py-3 px-4 border border-gray-300 rounded-xl shadow-sm bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none transition-all duration-300 hover:scale-[1.02] disabled:opacity-75 disabled:hover:scale-100 disabled:cursor-not-allowed"
              >
                {isGoogleRedirecting ? (
                  <Loader2 className="w-5 h-5 mr-2 animate-spin text-gray-500" />
                ) : (
                  <svg className="h-5 w-5 mr-2" viewBox="0 0 24 24">
                    <path
                      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                      fill="#4285F4"
                    />
                    <path
                      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                      fill="#34A853"
                    />
                    <path
                      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                      fill="#FBBC05"
                    />
                    <path
                      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                      fill="#EA4335"
                    />
                  </svg>
                )}
                {isGoogleRedirecting ? t('auth.redirecting') : 'Google'}
              </button>
            </div>
          </div>
          <div className="mt-8 text-center text-xs text-gray-400 italic leading-tight">
            {t('auth.legalWarning')}
          </div>
        </div>
      </div>
    </div>
  );
}
